import { useState } from 'react'
import {
  AlertCircle,
  Globe2,
  Loader2,
  Pencil,
  RefreshCw,
  Trash2,
  Upload,
  Wand2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { BrandProfile } from '@/features/settings/brand-api'
import type { VoiceAnalysisUpdate, VoiceSource, VoiceSourceType } from './api'
import {
  useAddWebsiteDomainVoiceSource,
  useConsolidateVoice,
  useDeleteVoiceSource,
  useReanalyzeVoiceSource,
  useUpdateVoiceAnalysis,
  useUploadVoiceSource,
  useVoiceSources,
} from './use-voice-sources'

const SOURCE_TYPE_LABELS: Record<VoiceSourceType, string> = {
  linkedin_pdf: 'LinkedIn-Profil (PDF)',
  own_text: 'Eigener Text',
  blog_post: 'Blog-Post',
  newsletter: 'Newsletter',
  book_excerpt: 'Buch-Auszug',
  transcript: 'Transkript',
  website_domain: 'Website-Domain',
}

const MAX_FILE_SIZE = 5 * 1024 * 1024

interface VoiceSourcesCardProps {
  profile: BrandProfile
}

// Heuristik: Typ aus Dateiname + Endung ableiten. User kann jederzeit ueberschreiben
// im Confirm-Step. Reihenfolge der Checks ist absichtlich — spezifischer zuerst.
function detectSourceType(filename: string): VoiceSourceType {
  const lower = filename.toLowerCase()
  if (lower.endsWith('.pdf')) {
    if (/(linkedin|profil)/.test(lower)) return 'linkedin_pdf'
    return 'linkedin_pdf' // Default fuer PDFs — meistens ist es ein LinkedIn-Export
  }
  if (/(newsletter|substack|beehiiv)/.test(lower)) return 'newsletter'
  if (/(blog|post|article)/.test(lower)) return 'blog_post'
  if (/(transcript|interview|talk|podcast)/.test(lower)) return 'transcript'
  if (/(buch|book|chapter|exzerpt)/.test(lower)) return 'book_excerpt'
  return 'own_text'
}

function stripExtension(filename: string): string {
  const dot = filename.lastIndexOf('.')
  return dot > 0 ? filename.slice(0, dot) : filename
}

interface PendingFile {
  id: string
  file: File
  sourceType: VoiceSourceType
  sourceName: string
  status: 'idle' | 'uploading' | 'error'
  errorMessage?: string
}

function newPendingId(): string {
  // crypto.randomUUID() ist in allen modernen Browsern (Chrome 92+, Safari 15.4+).
  // Fallback fuer aeltere Umgebungen / Edge-Cases ohne Crypto-API.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `p-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function VoiceSourcesCard({ profile }: VoiceSourcesCardProps) {
  const upload = useUploadVoiceSource(profile.id)
  const addWebsiteDomain = useAddWebsiteDomainVoiceSource(profile.id)
  const reanalyze = useReanalyzeVoiceSource(profile.id)
  const consolidate = useConsolidateVoice(profile.id)
  const remove = useDeleteVoiceSource(profile.id)

  const [pending, setPending] = useState<PendingFile[]>([])
  const [domainUrl, setDomainUrl] = useState(profile.domain_url ?? '')
  const [expandedError, setExpandedError] = useState<string | null>(null)
  const anyUploading = pending.some((p) => p.status === 'uploading')

  // Phase 1.8: waehrend consolidate laeuft polled die Source-Liste alle 3s,
  // damit User sehen kann wie Sources von 'uploaded' -> 'analyzing' -> 'analyzed' wechseln.
  const sourcesQuery = useVoiceSources(profile.id, {
    activePolling: consolidate.isPending,
  })

  const sources = sourcesQuery.data ?? []
  const va = profile.voice_analysis ?? {}
  const hasVoice =
    !!va.tone ||
    (va.hook_examples?.length ?? 0) > 0 ||
    (va.do_nots?.length ?? 0) > 0 ||
    !!va.structural_patterns

  const pendingCount = sources.filter((s) => s.status === 'uploaded').length
  const analyzingCount = sources.filter((s) => s.status === 'analyzing').length
  const analyzedCount = sources.filter((s) => s.status === 'analyzed').length

  const handleFilesPicked = (files: File[]) => {
    const accepted: PendingFile[] = []
    let oversized = 0
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        oversized++
        continue
      }
      accepted.push({
        id: newPendingId(),
        file,
        sourceType: detectSourceType(file.name),
        sourceName: stripExtension(file.name),
        status: 'idle',
      })
    }
    if (oversized > 0) {
      toast.error(
        oversized === 1
          ? 'Eine Datei über 5 MB — bitte verkleinern'
          : `${oversized} Dateien über 5 MB — bitte verkleinern`,
      )
    }
    if (accepted.length > 0) {
      setPending((prev) => [...prev, ...accepted])
    }
  }

  const updatePendingItem = (id: string, patch: Partial<PendingFile>) => {
    setPending((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)))
  }

  const removePendingItem = (id: string) => {
    setPending((prev) => prev.filter((p) => p.id !== id))
  }

  const cancelAllPending = () => {
    if (anyUploading) return
    setPending([])
  }

  const handleSubmitAll = async () => {
    if (pending.length === 0) return
    // Phase: alle idle->uploading setzen (errorMessage zuruecksetzen fuer Retry-Fall)
    setPending((prev) =>
      prev.map((p) => ({ ...p, status: 'uploading', errorMessage: undefined })),
    )

    // Snapshot der Items zum Iterieren (kein State-Read im Loop).
    const snapshot = pending

    const results = await Promise.allSettled(
      snapshot.map((p) =>
        upload
          .mutateAsync({
            file: p.file,
            sourceType: p.sourceType,
            sourceName: p.sourceName || undefined,
            skipAnalyze: true,
          })
          .then(() => p.id),
      ),
    )

    // Erfolgreiche Items verlassen die pending-Liste; fehlgeschlagene bleiben mit error-Status.
    setPending((prev) => {
      const next: PendingFile[] = []
      prev.forEach((p) => {
        const idx = snapshot.findIndex((s) => s.id === p.id)
        const r = idx >= 0 ? results[idx] : undefined
        if (!r) {
          // Item ist neu seit Submit-Klick — unveraendert behalten.
          next.push(p)
          return
        }
        if (r.status === 'rejected') {
          next.push({
            ...p,
            status: 'error',
            errorMessage:
              r.reason instanceof Error ? r.reason.message : 'Upload fehlgeschlagen',
          })
        }
        // fulfilled -> faellt aus der Liste raus
      })
      return next
    })

    const ok = results.filter((r) => r.status === 'fulfilled').length
    const fail = results.length - ok
    if (ok > 0 && fail === 0) {
      toast.success(
        ok === 1
          ? 'Quelle hochgeladen — bereit zur Analyse'
          : `${ok} Quellen hochgeladen — bereit zur Analyse`,
      )
    } else if (ok > 0) {
      toast.warning(`${ok} hochgeladen, ${fail} fehlgeschlagen`)
    } else {
      toast.error('Upload fehlgeschlagen')
    }
  }

  const handleReanalyze = async (sourceId: string) => {
    try {
      await reanalyze.mutateAsync(sourceId)
      toast.success('Erneut analysiert')
    } catch {
      toast.error('Analyse fehlgeschlagen')
    }
  }

  const handleConsolidate = async () => {
    try {
      const result = await consolidate.mutateAsync()
      const conf = typeof result.confidence === 'number' ? Math.round(result.confidence * 100) : 0
      const bedrock = result.bedrock_count
      const bedrockNote = typeof bedrock === 'number' ? `, ${bedrock} Bedrock-Patterns` : ''
      toast.success(
        `Voice konsolidiert (${result.source_count ?? 0} Quellen, Confidence ${conf}%${bedrockNote})`,
      )
    } catch {
      toast.error('Konsolidierung fehlgeschlagen')
    }
  }

  const handleAddDomain = async () => {
    const url = domainUrl.trim()
    if (!url) return
    try {
      await addWebsiteDomain.mutateAsync({ url, depth: 5, skipAnalyze: true })
      toast.success('Website als Voice-Quelle hinzugefügt')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Website-Crawl fehlgeschlagen')
    }
  }

  const handleDelete = async (source: VoiceSource) => {
    if (!confirm(`Quelle "${source.source_name}" wirklich löschen?`)) return
    try {
      await remove.mutateAsync(source.id)
      toast.success('Gelöscht')
    } catch {
      toast.error('Löschen fehlgeschlagen')
    }
  }

  const canConsolidate = (pendingCount > 0 || analyzedCount > 0) && !consolidate.isPending
  const consolidateLabel = (() => {
    if (consolidate.isPending) {
      if (analyzingCount > 0) {
        return `Analysiere (${analyzedCount + analyzingCount}/${pendingCount + analyzingCount + analyzedCount})…`
      }
      return 'Reasoning…'
    }
    if (pendingCount > 0) {
      return `Voice analysieren + konsolidieren (${pendingCount} neu)`
    }
    return 'Voice konsolidieren'
  })()

  return (
    <Card>
      <CardHeader>
        <CardTitle>Voice-Quellen — Wer schreibt diese Marke?</CardTitle>
        <p className="text-sm text-muted-foreground">
          Wird verwendet in: /linkedin, /x, /newsletter, /blog und Präsentations-Strategie.
          Quellen werden analysiert und zu einem konsistenten Voice-Anker verdichtet.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Lade Texte hoch, die so klingen, wie diese Marke klingen soll. MindKeep extrahiert daraus
          den Voice-Anker (Tonalität, Hook-Beispiele, Anti-Floskeln) und injiziert ihn in alle
          Outputs. Du kannst mehrere Quellen hochladen und sie dann in einem Rutsch analysieren.
        </p>

        <FilePickerArea onFilesPicked={handleFilesPicked} disabled={anyUploading} />

        <div className="flex flex-col gap-2 rounded-md border border-border bg-muted/20 p-3 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <Label className="text-sm text-muted-foreground">Website als Voice-Quelle</Label>
            <Input
              className="mt-1"
              type="url"
              value={domainUrl}
              onChange={(e) => setDomainUrl(e.target.value)}
              placeholder="https://example.com"
            />
          </div>
          <Button
            variant="outline"
            disabled={!domainUrl.trim() || addWebsiteDomain.isPending}
            onClick={() => void handleAddDomain()}
          >
            {addWebsiteDomain.isPending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Globe2 className="mr-1.5 h-4 w-4" />
            )}
            Website lesen
          </Button>
        </div>

        {pending.length > 0 && (
          <PendingUploadStack
            items={pending}
            anyUploading={anyUploading}
            onItemChange={updatePendingItem}
            onItemRemove={removePendingItem}
            onSubmitAll={() => void handleSubmitAll()}
            onCancelAll={cancelAllPending}
          />
        )}

        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label className="text-sm text-muted-foreground">
              Quellen ({sources.length})
              {pendingCount > 0 && ` · ${pendingCount} neu`}
              {analyzingCount > 0 && ` · ${analyzingCount} analysiert gerade`}
              {analyzedCount > 0 && ` · ${analyzedCount} fertig`}
            </Label>
            <Button
              size="sm"
              variant="outline"
              disabled={!canConsolidate}
              onClick={() => void handleConsolidate()}
            >
              {consolidate.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="mr-1.5 h-4 w-4" />
              )}
              {consolidateLabel}
            </Button>
          </div>

          {sourcesQuery.isLoading ? (
            <p className="text-xs text-muted-foreground">Lädt…</p>
          ) : sources.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Noch keine Quellen. Lade z.B. dein LinkedIn-Profil als PDF hoch (Profil → Mehr →
              Profil-PDF speichern).
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {sources.map((s) => {
                const isErrorExpanded = expandedError === s.id
                return (
                  <li key={s.id} className="px-3 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-medium">{s.source_name}</span>
                          <StatusBadge status={s.status} />
                        </div>
                        <p className="font-mono text-[11px] text-muted-foreground">
                          {SOURCE_TYPE_LABELS[s.source_type]} ·{' '}
                          {new Date(s.uploaded_at).toLocaleString('de-CH', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </p>
                      </div>
                      {s.status === 'error' && s.error_message && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setExpandedError(isErrorExpanded ? null : s.id)}
                          title="Fehler-Details anzeigen"
                        >
                          <AlertCircle className="h-4 w-4 text-red-600" />
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        disabled={reanalyze.isPending || s.status === 'analyzing'}
                        onClick={() => void handleReanalyze(s.id)}
                        title="Erneut analysieren"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        disabled={remove.isPending}
                        onClick={() => void handleDelete(s)}
                        title="Löschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {isErrorExpanded && s.error_message && (
                      <pre className="mt-2 whitespace-pre-wrap rounded border border-red-200 bg-red-50 p-2 font-mono text-[11px] text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
                        {s.error_message}
                      </pre>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {hasVoice && <VoiceAnalysisSummary va={va} brandId={profile.id} />}
      </CardContent>
    </Card>
  )
}

function FilePickerArea({
  onFilesPicked,
  disabled,
}: {
  onFilesPicked: (files: File[]) => void
  disabled?: boolean
}) {
  return (
    <label
      className={`flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border px-3 py-6 text-sm text-muted-foreground hover:bg-muted ${
        disabled ? 'pointer-events-none opacity-60' : ''
      }`}
    >
      <Upload className="h-4 w-4" />
      <span>PDF, Markdown oder Text wählen (mehrere möglich, max 5 MB pro Datei)</span>
      <input
        type="file"
        multiple
        accept=".pdf,.md,.markdown,.txt,application/pdf,text/markdown,text/plain"
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          if (files.length > 0) onFilesPicked(files)
          e.target.value = ''
        }}
      />
    </label>
  )
}

function PendingUploadStack({
  items,
  anyUploading,
  onItemChange,
  onItemRemove,
  onSubmitAll,
  onCancelAll,
}: {
  items: PendingFile[]
  anyUploading: boolean
  onItemChange: (id: string, patch: Partial<PendingFile>) => void
  onItemRemove: (id: string) => void
  onSubmitAll: () => void
  onCancelAll: () => void
}) {
  return (
    <div className="space-y-2">
      {items.map((item) => (
        <PendingUploadItem
          key={item.id}
          item={item}
          disabled={anyUploading}
          onChange={(patch) => onItemChange(item.id, patch)}
          onRemove={() => onItemRemove(item.id)}
        />
      ))}
      <div className="flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
        <p className="text-[11px] text-muted-foreground">
          Typ + Name wurden aus dem Dateinamen erkannt — bei Bedarf pro Eintrag anpassen. Klick
          auf "Alle hochladen" persistiert die Quellen, die Analyse startet erst beim
          Konsolidieren.
        </p>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button variant="ghost" size="sm" disabled={anyUploading} onClick={onCancelAll}>
            Alle abbrechen
          </Button>
          <Button size="sm" disabled={anyUploading || items.length === 0} onClick={onSubmitAll}>
            {anyUploading && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {anyUploading ? 'Lade hoch…' : `Alle hochladen (${items.length})`}
          </Button>
        </div>
      </div>
    </div>
  )
}

function PendingUploadItem({
  item,
  disabled,
  onChange,
  onRemove,
}: {
  item: PendingFile
  disabled: boolean
  onChange: (patch: Partial<PendingFile>) => void
  onRemove: () => void
}) {
  const sizeKb = Math.round(item.file.size / 1024)
  const isUploading = item.status === 'uploading'
  const isError = item.status === 'error'
  const inputsDisabled = disabled || isUploading

  return (
    <div
      className={`rounded-md border p-4 space-y-3 ${
        isError
          ? 'border-red-400 bg-red-50 dark:border-red-900 dark:bg-red-950/30'
          : 'border-primary/40 bg-primary/5'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{item.file.name}</p>
          <p className="font-mono text-[11px] text-muted-foreground">
            {sizeKb} KB
            {isUploading && ' · wird hochgeladen…'}
            {isError && ' · Fehler'}
          </p>
        </div>
        {isUploading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <Button size="icon" variant="ghost" onClick={onRemove} title="Entfernen">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-sm text-muted-foreground">Typ</Label>
          <select
            value={item.sourceType}
            disabled={inputsDisabled}
            onChange={(e) => onChange({ sourceType: e.target.value as VoiceSourceType })}
            className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            {Object.entries(SOURCE_TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-sm text-muted-foreground">Name</Label>
          <Input
            value={item.sourceName}
            disabled={inputsDisabled}
            onChange={(e) => onChange({ sourceName: e.target.value })}
            className="mt-1"
          />
        </div>
      </div>
      {isError && item.errorMessage && (
        <p className="rounded border border-red-300 bg-white/60 px-2 py-1 text-[11px] text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
          {item.errorMessage}
        </p>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: VoiceSource['status'] }) {
  if (status === 'analyzed') {
    return (
      <Badge variant="outline" className="border-green-500 text-green-700 dark:text-green-300">
        analysiert
      </Badge>
    )
  }
  if (status === 'analyzing') {
    return (
      <Badge variant="outline" className="border-blue-500 text-blue-700 dark:text-blue-300">
        <Loader2 className="mr-1 h-3 w-3 animate-spin" /> analysiere
      </Badge>
    )
  }
  if (status === 'error') {
    return (
      <Badge variant="outline" className="border-red-500 text-red-700 dark:text-red-300">
        Fehler
      </Badge>
    )
  }
  return <Badge variant="outline">hochgeladen</Badge>
}

function VoiceAnalysisSummary({
  va,
  brandId,
}: {
  va: NonNullable<BrandProfile['voice_analysis']>
  brandId: string
}) {
  const [isEditing, setIsEditing] = useState(false)
  const updateMutation = useUpdateVoiceAnalysis(brandId)

  const confidence = typeof va.confidence === 'number' ? Math.round(va.confidence * 100) : null
  const wasEdited = !!(va as { last_edited_by_user_at?: string }).last_edited_by_user_at

  return (
    <div className="rounded-md border border-border bg-muted/30 p-3 space-y-2 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-0.5">
          <p className="font-semibold">Konsolidierte Voice</p>
          <p className="text-[11px] text-muted-foreground">
            Wird verwendet in: /linkedin, /x, /newsletter, /blog, /presentation. Nicht im normalen
            Chat oder /research.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {confidence !== null && (
            <ConfidenceBadge
              confidence={confidence}
              sourceCount={va.source_count ?? 0}
              lastUpdated={va.last_updated}
              wasEdited={wasEdited}
            />
          )}
          {!isEditing && (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setIsEditing(true)}
              title="Voice editieren"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {isEditing ? (
        <VoiceAnalysisEditor
          va={va}
          isSaving={updateMutation.isPending}
          onSave={async (update) => {
            try {
              await updateMutation.mutateAsync(update)
              toast.success('Voice gespeichert (User-Override aktiv)')
              setIsEditing(false)
            } catch (err) {
              toast.error(err instanceof Error ? err.message : 'Speichern fehlgeschlagen')
            }
          }}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <VoiceAnalysisReadView va={va} />
      )}
    </div>
  )
}

function ConfidenceBadge({
  confidence,
  sourceCount,
  lastUpdated,
  wasEdited,
}: {
  confidence: number
  sourceCount: number
  lastUpdated?: string
  wasEdited: boolean
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="rounded-md bg-background px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground border border-border cursor-help">
            Conf {confidence}% · {sourceCount} Q.
            {wasEdited && ' · edit'}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs space-y-1">
          <p className="font-medium">Confidence: {confidence}%</p>
          <p className="text-muted-foreground">Quellen: {sourceCount}/4 saturiert · Gewicht 30%</p>
          <p className="text-muted-foreground">Text-Volumen: 100k chars saturiert · Gewicht 20%</p>
          <p className="text-muted-foreground">Cross-Source-Konsistenz · Gewicht 40%</p>
          <p className="text-muted-foreground">Vielfalt-Bonus bei {'>'}1 Quelle · Gewicht 10%</p>
          {lastUpdated && <p className="text-muted-foreground">Aktualisiert: {new Date(lastUpdated).toLocaleString('de-CH')}</p>}
          {wasEdited && <p className="text-amber-500">User-edited (manueller Override aktiv)</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function VoiceAnalysisReadView({
  va,
}: {
  va: NonNullable<BrandProfile['voice_analysis']>
}) {
  const extra = va as NonNullable<BrandProfile['voice_analysis']> & {
    argumentation_patterns?: string
    bedrock_count?: number
    evidence_summary?: string
    consolidator_notes?: string
  }
  return (
    <div className="space-y-2">
      {extra.consolidator_notes && (
        <div className="rounded border border-amber-400/50 bg-amber-50 px-2 py-1 text-[11px] text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <span className="font-medium">Reasoning-Hinweis:</span> {extra.consolidator_notes}
        </div>
      )}
      {va.tone && (
        <p>
          <span className="font-medium">Tone:</span> {va.tone}
        </p>
      )}
      {va.structural_patterns && (
        <p>
          <span className="font-medium">Struktur (Mikro):</span> {va.structural_patterns}
        </p>
      )}
      {extra.argumentation_patterns && (
        <p>
          <span className="font-medium">Argumentation (Makro):</span>{' '}
          {extra.argumentation_patterns}
        </p>
      )}
      {va.hook_examples && va.hook_examples.length > 0 && (
        <div>
          <span className="font-medium">Hook-Patterns:</span>
          <ul className="ml-3 mt-0.5 list-disc">
            {va.hook_examples.map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ul>
        </div>
      )}
      {va.do_nots && va.do_nots.length > 0 && (
        <p>
          <span className="font-medium">Niemals:</span> {va.do_nots.join(' · ')}
        </p>
      )}
      {va.usps && va.usps.length > 0 && (
        <p>
          <span className="font-medium">USPs:</span> {va.usps.join(' · ')}
        </p>
      )}
      {va.language_variant && (
        <p>
          <span className="font-medium">Sprachvariante:</span> {va.language_variant}
        </p>
      )}
      {(typeof extra.bedrock_count === 'number' || extra.evidence_summary) && (
        <p className="pt-1 text-[10px] text-muted-foreground border-t border-border/50">
          {typeof extra.bedrock_count === 'number' && (
            <>
              <span className="font-medium">Bedrock:</span> {extra.bedrock_count} Patterns (Soll
              5-8)
            </>
          )}
          {extra.evidence_summary && (
            <>
              {' · '}
              {extra.evidence_summary}
            </>
          )}
        </p>
      )}
    </div>
  )
}

function linesToList(s: string): string[] {
  return s
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
}

function VoiceAnalysisEditor({
  va,
  isSaving,
  onSave,
  onCancel,
}: {
  va: NonNullable<BrandProfile['voice_analysis']>
  isSaving: boolean
  onSave: (update: VoiceAnalysisUpdate) => void | Promise<void>
  onCancel: () => void
}) {
  const [tone, setTone] = useState(va.tone ?? '')
  const [structural, setStructural] = useState(va.structural_patterns ?? '')
  const [doNots, setDoNots] = useState((va.do_nots ?? []).join('\n'))
  const [hookExamples, setHookExamples] = useState((va.hook_examples ?? []).join('\n'))
  const [usps, setUsps] = useState((va.usps ?? []).join('\n'))

  const handleSave = () => {
    void onSave({
      tone: tone.trim() || undefined,
      structural_patterns: structural.trim() || undefined,
      do_nots: linesToList(doNots),
      hook_examples: linesToList(hookExamples),
      usps: linesToList(usps),
    })
  }

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-[11px] font-medium">Tone (Stil-Beschreibung)</Label>
        <Textarea
          value={tone}
          onChange={(e) => setTone(e.target.value)}
          disabled={isSaving}
          rows={2}
          className="mt-1 text-xs"
          placeholder="z.B. Verdichtet, sachlich. Zahlen statt Adjektive. Kurze Hauptsaetze."
        />
      </div>
      <div>
        <Label className="text-[11px] font-medium">Struktur (Pacing, Satzbau, Reihenfolge)</Label>
        <Textarea
          value={structural}
          onChange={(e) => setStructural(e.target.value)}
          disabled={isSaving}
          rows={2}
          className="mt-1 text-xs"
          placeholder="z.B. Punkt-Pfeil-Format. Hauptsatz voran, Doppelpunkt-Bruch für Pointe."
        />
      </div>
      <div>
        <Label className="text-[11px] font-medium">Hook-Patterns (eine pro Zeile)</Label>
        <Textarea
          value={hookExamples}
          onChange={(e) => setHookExamples(e.target.value)}
          disabled={isSaving}
          rows={4}
          className="mt-1 font-mono text-xs"
          placeholder={'Provokante "Warum gerade jetzt <TOPIC>?"-Frage als Opener\n...'}
        />
      </div>
      <div>
        <Label className="text-[11px] font-medium">Do-Nots / Floskeln (eine pro Zeile)</Label>
        <Textarea
          value={doNots}
          onChange={(e) => setDoNots(e.target.value)}
          disabled={isSaving}
          rows={4}
          className="mt-1 font-mono text-xs"
          placeholder={'thrilled to\ndelve into\nin todays fast-paced world'}
        />
      </div>
      <div>
        <Label className="text-[11px] font-medium">USPs (kurze Stichpunkte, eine pro Zeile)</Label>
        <Textarea
          value={usps}
          onChange={(e) => setUsps(e.target.value)}
          disabled={isSaving}
          rows={3}
          className="mt-1 font-mono text-xs"
          placeholder={'20 Jahre Maritim-Industrie\nKMU-Praktiker\nEU-Hosting-Fokus'}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" disabled={isSaving} onClick={onCancel}>
          Abbrechen
        </Button>
        <Button size="sm" disabled={isSaving} onClick={handleSave}>
          {isSaving && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
          {isSaving ? 'Speichere…' : 'Voice speichern (User-Override)'}
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground">
        Hinweis: Nach dem Speichern markiert MindKeep die Voice als User-edited. Bei Reanalyze
        bleibt deine Version erhalten — du siehst dann den AI-Vorschlag separat zur Auswahl.
      </p>
    </div>
  )
}
